import React, { useState, useEffect } from 'react';
import { useApiKey } from '../context/ApiKeyContext';
import { validateLlmApiKey } from '../services/api';

type KeyStatus = 'idle' | 'valid' | 'invalid';

const ApiKeyModal: React.FC = () => {
  const { hasKey, maskedKey, setApiKey, clearApiKey, showKeyModal, closeKeyModal } = useApiKey();
  const [keyInput, setKeyInput] = useState('');
  const [error, setError] = useState('');
  const [keyStatus, setKeyStatus] = useState<KeyStatus>('idle');
  const [invalidDetail, setInvalidDetail] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    if (showKeyModal) {
      setKeyInput('');
      setError('');
      setInvalidDetail('');
      setKeyStatus(hasKey ? 'valid' : 'idle');
    }
  }, [showKeyModal, hasKey]);

  if (!showKeyModal) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmed = keyInput.trim();
    if (!trimmed) {
      setError('Please enter an API key.');
      return;
    }

    setIsValidating(true);
    try {
      const result = await validateLlmApiKey(trimmed);
      if (!result.valid) {
        setKeyStatus('invalid');
        setInvalidDetail(result.error || 'Invalid API key. Check your key and try again.');
        return;
      }

      setApiKey(trimmed);
      setKeyInput('');
      setInvalidDetail('');
      closeKeyModal();
    } catch (err) {
      setKeyStatus('invalid');
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { detail?: string; error?: string } } };
        const detail = axiosErr.response?.data?.detail || axiosErr.response?.data?.error;
        setInvalidDetail(
          detail || 'Could not validate API key. Make sure the backend is running.',
        );
      } else if (err instanceof Error) {
        setInvalidDetail(err.message);
      } else {
        setInvalidDetail('Could not validate API key. Make sure the backend is running.');
      }
    } finally {
      setIsValidating(false);
    }
  };

  const handleClear = () => {
    clearApiKey();
    setKeyInput('');
    setError('');
    setKeyStatus('idle');
    setInvalidDetail('');
    closeKeyModal();
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.92)' }}
    >
      <div
        style={{
          backgroundColor: '#000000',
          border: keyStatus === 'invalid' ? '2px solid #e74c3c' : '2px solid #ffffff',
          borderRadius: 0,
          maxWidth: '32rem',
          width: '100%',
          padding: '2rem',
        }}
      >
        <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
          <h2
            style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#ffffff',
              marginBottom: '0.5rem',
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            OpenAI API Key
          </h2>
          <p
            style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.875rem',
              fontFamily: "'Inter', sans-serif",
              lineHeight: 1.5,
            }}
          >
            Add an OpenAI API Key
          </p>
        </div>

        {keyStatus === 'valid' && maskedKey && (
          <div
            style={{
              backgroundColor: 'rgba(46, 204, 113, 0.1)',
              border: '1px solid #2ecc71',
              borderRadius: 0,
              padding: '0.75rem',
              marginBottom: '1.25rem',
            }}
          >
            <p
              style={{
                color: '#2ecc71',
                fontSize: '0.875rem',
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                marginBottom: '0.25rem',
              }}
            >
              Valid API key
            </p>
            <p
              style={{
                color: '#2ecc71',
                fontSize: '0.875rem',
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Current key: {maskedKey}
            </p>
          </div>
        )}

        {keyStatus === 'invalid' && (
          <div
            style={{
              backgroundColor: 'rgba(231, 76, 60, 0.1)',
              border: '1px solid #e74c3c',
              borderRadius: 0,
              padding: '0.75rem',
              marginBottom: '1.25rem',
            }}
          >
            <p
              style={{
                color: '#e74c3c',
                fontSize: '0.875rem',
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                marginBottom: '0.35rem',
              }}
            >
              Invalid API key
            </p>
            <p
              style={{
                color: '#e74c3c',
                fontSize: '0.875rem',
                fontFamily: "'Inter', sans-serif",
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              {invalidDetail}
            </p>
          </div>
        )}

        {error && (
          <div
            style={{
              backgroundColor: 'rgba(231, 76, 60, 0.1)',
              border: '1px solid #e74c3c',
              borderRadius: 0,
              padding: '0.75rem',
              marginBottom: '1.25rem',
            }}
          >
            <p style={{ color: '#e74c3c', fontSize: '0.875rem', fontFamily: "'Inter', sans-serif" }}>
              {error}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="off">
          <label
            htmlFor="promptx-llm-api-key"
            style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#ffffff',
              marginBottom: '0.5rem',
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.05em',
            }}
          >
            API KEY
          </label>
          <input
            id="promptx-llm-api-key"
            name="promptx-llm-api-key"
            type="text"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="Paste your API key"
            autoComplete="off"
            data-1p-ignore="true"
            data-lpignore="true"
            data-bwignore
            data-form-type="other"
            disabled={isValidating}
            style={{
              width: '100%',
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.4)',
              color: '#ffffff',
              padding: '0.75rem',
              fontSize: '0.9rem',
              fontFamily: "'Inter', sans-serif",
              borderRadius: 0,
              marginBottom: '1.25rem',
              boxSizing: 'border-box',
              opacity: isValidating ? 0.6 : 1,
              WebkitTextSecurity: 'disc',
            } as React.CSSProperties}
          />

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              type="submit"
              disabled={isValidating}
              style={{
                flex: 1,
                background: isValidating ? 'rgba(255, 255, 255, 0.5)' : '#ffffff',
                border: '1px solid #ffffff',
                color: '#000000',
                padding: '0.75rem 1rem',
                fontSize: '0.85rem',
                fontWeight: 600,
                letterSpacing: '0.05em',
                cursor: isValidating ? 'not-allowed' : 'pointer',
                fontFamily: "'Inter', sans-serif",
                borderRadius: 0,
                textTransform: 'uppercase',
                opacity: isValidating ? 0.7 : 1,
              }}
            >
              {isValidating ? 'Validating...' : hasKey ? 'Update Key' : 'Save Key'}
            </button>
            <button
              type="button"
              onClick={closeKeyModal}
              disabled={isValidating}
              style={{
                flex: 1,
                background: 'transparent',
                border: '1px solid #ffffff',
                color: '#ffffff',
                padding: '0.75rem 1rem',
                fontSize: '0.85rem',
                fontWeight: 600,
                letterSpacing: '0.05em',
                cursor: isValidating ? 'not-allowed' : 'pointer',
                fontFamily: "'Inter', sans-serif",
                borderRadius: 0,
                textTransform: 'uppercase',
                opacity: isValidating ? 0.7 : 1,
              }}
            >
              Cancel
            </button>
          </div>
        </form>

        {hasKey && (
          <button
            type="button"
            onClick={handleClear}
            disabled={isValidating}
            style={{
              width: '100%',
              marginTop: '1rem',
              background: 'transparent',
              border: '1px solid #e74c3c',
              color: '#e74c3c',
              padding: '0.6rem 1rem',
              fontSize: '0.8rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              cursor: isValidating ? 'not-allowed' : 'pointer',
              fontFamily: "'Inter', sans-serif",
              borderRadius: 0,
              textTransform: 'uppercase',
              opacity: isValidating ? 0.7 : 1,
            }}
          >
            Remove Key
          </button>
        )}
      </div>
    </div>
  );
};

export default ApiKeyModal;
